import React, { useState, useEffect } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import {
    Button,
    TextField,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    CircularProgress,
    MenuItem,
} from "@material-ui/core";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
    btnWrapper: {
        position: "relative",
    },
    buttonProgress: {
        color: green[500],
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
}));

const HubNotificaMeSchema = Yup.object().shape({
    nome: Yup.string()
        .trim()
        .min(2, "El nombre debe tener al menos 2 caracteres")
        .required("El nombre es obligatorio"),
    token: Yup.string()
        .trim()
        .min(6, "El token debe tener al menos 6 caracteres")
        .required("El token es obligatorio"),
    tipo: Yup.string()
        .oneOf(["Facebook", "Instagram", "Webchat"], "Tipo inválido")
        .required("El tipo es obligatorio"),
});

const HubNotificaMeDialog = ({ open, onClose, hubnotificameId }) => {
    const classes = useStyles();

    const initialState = {
        nome: "",
        token: "",
        tipo: "",
    };

    const [hubnotificame, setHubNotificaMe] = useState(initialState);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const fetchData = async () => {
            if (!open) return;
            if (!hubnotificameId) {
                setHubNotificaMe(initialState);
                return;
            }
            setLoading(true);
            try {
                const { data } = await api.get(`/hub-notificame/${hubnotificameId}`);
                if (cancelled) return;
                setHubNotificaMe({
                    nome: data.nome || "",
                    token: data.token || "",
                    tipo: data.tipo || "",
                });
            } catch (err) {
                toastError(err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        fetchData();
        return () => {
            cancelled = true;
        };
    }, [hubnotificameId, open]);

    const handleClose = () => {
        setHubNotificaMe(initialState);
        onClose();
    };

    const handleSaveHubNotificaMe = async (values) => {
        const payload = {
            nome: values.nome.trim(),
            token: values.token.trim(),
            tipo: values.tipo,
        };
        try {
            if (hubnotificameId) {
                await api.put(`/hub-notificame/${hubnotificameId}`, payload);
                toast.success("Canal actualizado correctamente");
            } else {
                await api.post("/hub-notificame", payload);
                toast.success("Token agregado correctamente");
            }
            handleClose();
        } catch (err) {
            toastError(err);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle>{hubnotificameId ? "Editar canal Meta" : "Agregar token Meta"}</DialogTitle>
            <Formik
                initialValues={hubnotificame}
                enableReinitialize
                validationSchema={HubNotificaMeSchema}
                onSubmit={handleSaveHubNotificaMe}
            >
                {({ touched, errors, isSubmitting }) => (
                    <Form>
                        <DialogContent dividers>
                            <Field
                                as={TextField}
                                label="Nombre"
                                name="nome"
                                error={touched.nome && Boolean(errors.nome)}
                                helperText={touched.nome && errors.nome}
                                variant="outlined"
                                margin="dense"
                                fullWidth
                                disabled={loading}
                            />

                            <Field
                                as={TextField}
                                label="Token"
                                name="token"
                                error={touched.token && Boolean(errors.token)}
                                helperText={touched.token && errors.token}
                                variant="outlined"
                                margin="dense"
                                fullWidth
                                disabled={loading}
                            />

                            <Field
                                as={TextField}
                                select
                                label="Tipo"
                                name="tipo"
                                error={touched.tipo && Boolean(errors.tipo)}
                                helperText={touched.tipo && errors.tipo}
                                variant="outlined"
                                margin="dense"
                                fullWidth
                                disabled={loading}
                            >
                                <MenuItem value="Facebook">Facebook</MenuItem>
                                <MenuItem value="Instagram">Instagram</MenuItem>
                                <MenuItem value="Webchat">WebChat</MenuItem>
                            </Field>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={handleClose}
                                color="secondary"
                                disabled={isSubmitting}
                                variant="outlined"
                            >
                                Cancelar
                            </Button>
                            <Button
                                type="submit"
                                color="primary"
                                disabled={isSubmitting || loading}
                                variant="contained"
                                className={classes.btnWrapper}
                            >
                                {hubnotificameId ? "Guardar" : "Agregar"}
                                {isSubmitting && (
                                    <CircularProgress
                                        size={24}
                                        className={classes.buttonProgress}
                                    />
                                )}
                            </Button>
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
};

export default HubNotificaMeDialog;
